import type { Course, ControlPoint } from '@rcs/data-schema';
import { LIMITS, MAX_COORDINATE } from '@rcs/data-schema';
import { createCatmullRom } from '@rcs/track-geometry';
import type { Vec3 } from '@rcs/domain';
import type { Command } from './command';

/**
 * エディターの状態。選択などの一時的な状態は、保存データ（`Course`）に含めない（データ形式仕様 7 章）。
 */
export interface EditorState {
  readonly course: Course;
  /** 選択中の制御点の ID */
  readonly selectedId: string | undefined;
}

/** コースから、何も選択していないエディターの状態を作る。 */
export function createEditorState(course: Course): EditorState {
  return { course, selectedId: undefined };
}

/** ID から制御点を探す。 */
export function findControlPoint(state: EditorState, id: string): ControlPoint | undefined {
  return state.course.controlPoints.find((point) => point.id === id);
}

/** 選択中の制御点。 */
export function selectedControlPoint(state: EditorState): ControlPoint | undefined {
  return state.selectedId === undefined ? undefined : findControlPoint(state, state.selectedId);
}

/** 制御点を選択する。`undefined`、または存在しない ID なら選択を解除する。 */
export function selectControlPoint(state: EditorState, id: string | undefined): EditorState {
  const selectedId = id !== undefined && findControlPoint(state, id) !== undefined ? id : undefined;
  return selectedId === state.selectedId ? state : { ...state, selectedId };
}

/** 位置を、有効な範囲（有限・絶対値が {@link MAX_COORDINATE} 以下）に収める。 */
export function clampPosition(position: Vec3): [number, number, number] {
  const clamp = (value: number): number =>
    Number.isFinite(value) ? Math.min(MAX_COORDINATE, Math.max(-MAX_COORDINATE, value)) : 0;
  return [clamp(position[0]), clamp(position[1]), clamp(position[2])];
}

/**
 * 制御点の位置を変える（履歴には積まない）。存在しない ID なら、状態をそのまま返す。
 * 位置は有効な範囲に収める。
 */
export function setControlPointPosition(
  state: EditorState,
  id: string,
  position: Vec3,
): EditorState {
  if (findControlPoint(state, id) === undefined) return state;
  const next = clampPosition(position);
  return {
    ...state,
    course: {
      ...state.course,
      controlPoints: state.course.controlPoints.map((point) =>
        point.id === id ? { ...point, position: next } : point,
      ),
    },
  };
}

/** 制御点を `from` から `to` へ動かした操作（Undo / Redo の単位）。 */
export function moveControlPointCommand(id: string, from: Vec3, to: Vec3): Command<EditorState> {
  return {
    label: '制御点を移動',
    apply: (state) => setControlPointPosition(state, id, to),
    revert: (state) => setControlPointPosition(state, id, from),
  };
}

/** 新しい制御点を置く間隔 [m]（コースの末尾に追加するとき、末尾の向きへ延ばす長さ）。 */
export const APPEND_DISTANCE = 10;

/** 制御点 ID の接頭辞。`c1`, `c2`, … */
const ID_PREFIX = 'c';

/** コース内で使われていない、新しい制御点の ID（`c` + 既存の最大の番号 + 1）。 */
export function nextControlPointId(course: Course): string {
  let max = 0;
  for (const point of course.controlPoints) {
    const match = /^c(\d+)$/.exec(point.id);
    if (match) max = Math.max(max, Number(match[1]));
  }
  let candidate = `${ID_PREFIX}${String(max + 1)}`;
  // 番号以外の形の ID と衝突しないよう、念のため確認する
  while (course.controlPoints.some((point) => point.id === candidate)) {
    max += 1;
    candidate = `${ID_PREFIX}${String(max + 1)}`;
  }
  return candidate;
}

/** コースの制御点の数の上限。 */
function maxPoints(): number {
  return LIMITS.maxControlPoints;
}

/** 追加できるか（制御点の数が上限未満）。 */
export function canAddControlPoint(state: EditorState): boolean {
  return state.course.controlPoints.length < maxPoints();
}

/** 削除できるか（削除後も、開いたコースは 2 点以上、周回コースは 3 点以上）。 */
export function canRemoveControlPoint(state: EditorState, id: string): boolean {
  if (findControlPoint(state, id) === undefined) return false;
  const minimum = state.course.closed ? 3 : 2;
  return state.course.controlPoints.length - 1 >= minimum;
}

/** 挿入する制御点と、挿入位置（番号）。 */
export interface Insertion {
  readonly index: number;
  readonly point: ControlPoint;
}

/**
 * 制御点の挿入を計画する。選択中の制御点（または `afterId`）の次に入れる。選択がなければ末尾。
 *
 * - 次の制御点がある（または周回コース）：2 点の間の、曲線上の中点に置く。バンク角は両隣の平均
 * - 開いたコースの末尾：末尾の向きへ {@link APPEND_DISTANCE} だけ延ばした位置に置く
 * - 区間種別・パラメータは、直前の制御点のものを引き継ぐ（区間を 2 つに分けても、力モデルは変わらない）
 *
 * 数の上限に達していれば `undefined`。
 */
export function planInsertion(state: EditorState, afterId?: string): Insertion | undefined {
  if (!canAddControlPoint(state)) return undefined;
  const { course } = state;
  const points = course.controlPoints;
  const target = afterId ?? state.selectedId;
  const found = target === undefined ? -1 : points.findIndex((point) => point.id === target);
  const afterIndex = found >= 0 ? found : points.length - 1;
  const previous = points[afterIndex];
  if (!previous) return undefined;
  const isLast = afterIndex === points.length - 1;
  const next = isLast ? (course.closed ? points[0] : undefined) : points[afterIndex + 1];

  let position: Vec3;
  let bank: number;
  if (next) {
    const curve = createCatmullRom(
      points.map((point) => point.position),
      { closed: course.closed },
    );
    position = curve.position(afterIndex + 0.5);
    bank = (previous.bank + next.bank) / 2;
  } else {
    const before = points[afterIndex - 1];
    const dx = before ? previous.position[0] - before.position[0] : APPEND_DISTANCE;
    const dy = before ? previous.position[1] - before.position[1] : 0;
    const dz = before ? previous.position[2] - before.position[2] : 0;
    const length = Math.hypot(dx, dy, dz);
    const scale = length > 0 ? APPEND_DISTANCE / length : 0;
    position =
      length > 0
        ? [
            previous.position[0] + dx * scale,
            previous.position[1] + dy * scale,
            previous.position[2] + dz * scale,
          ]
        : [previous.position[0] + APPEND_DISTANCE, previous.position[1], previous.position[2]];
    bank = previous.bank;
  }
  return {
    index: afterIndex + 1,
    point: { ...previous, id: nextControlPointId(course), position: clampPosition(position), bank },
  };
}

/** 制御点を挿入した状態（挿入した点を選択する）。 */
function withInserted(state: EditorState, insertion: Insertion): EditorState {
  const points = [...state.course.controlPoints];
  points.splice(insertion.index, 0, insertion.point);
  return {
    ...state,
    course: { ...state.course, controlPoints: points },
    selectedId: insertion.point.id,
  };
}

/** 制御点を取り除いた状態。選択は、`selectedId` に置き換える。 */
function withRemoved(state: EditorState, id: string, selectedId: string | undefined): EditorState {
  return {
    ...state,
    course: {
      ...state.course,
      controlPoints: state.course.controlPoints.filter((point) => point.id !== id),
    },
    selectedId,
  };
}

/**
 * 制御点を追加する操作（Undo / Redo の単位）。追加した点を選択する。
 * 追加できなければ（数の上限）`undefined`。
 */
export function addControlPointCommand(
  state: EditorState,
  afterId?: string,
): Command<EditorState> | undefined {
  const insertion = planInsertion(state, afterId);
  if (!insertion) return undefined;
  const previousSelection = state.selectedId;
  return {
    label: '制御点を追加',
    apply: (current) => withInserted(current, insertion),
    revert: (current) =>
      withRemoved(
        current,
        insertion.point.id,
        previousSelection !== undefined && previousSelection !== insertion.point.id
          ? previousSelection
          : undefined,
      ),
  };
}

/**
 * 制御点を削除する操作（Undo / Redo の単位）。削除後は、直前の制御点（先頭なら次の制御点）を選択する。
 * 削除できなければ（点の数が下限）`undefined`。
 */
export function removeControlPointCommand(
  state: EditorState,
  id: string,
): Command<EditorState> | undefined {
  if (!canRemoveControlPoint(state, id)) return undefined;
  const points = state.course.controlPoints;
  const index = points.findIndex((point) => point.id === id);
  const removed = points[index];
  if (!removed) return undefined;
  const neighbour = points[index - 1] ?? points[index + 1];
  const previousSelection = state.selectedId;
  return {
    label: '制御点を削除',
    apply: (current) => withRemoved(current, id, neighbour?.id),
    revert: (current) => {
      const restored = withInserted(current, { index, point: removed });
      return { ...restored, selectedId: previousSelection === id ? id : previousSelection };
    },
  };
}
